// # The current schema
//
// This folder is always called `current` and is never version-named. The
// version it represents is the string in `./version.ts`; which version that is
// changes over time, and this folder does not.
//
// The asymmetry is deliberate: **code needs one live shape; data needs many.**
// Only the depot holds several versions at once
// (`content/{version}/{deployment}/`), so only the depot's paths carry the
// version.
//
// While a migration is in flight, a `prev/` folder sits beside this one — a
// copy of `current/` taken before the change — so the migration can read the
// old types and stay type-checked end to end. It is deleted once every depot
// is off the old version. See
// `.claude/skills/bump-schema-version/SKILL.md`.

// Construction sites use `SCHEMA_VERSION` rather than a literal, so a version
// bump is a one-line change in `version.ts`.
export * from "./version";

export * from "./core";
export * as core from "./core";

export type { Symbol } from "./symbol";
export * as symbol from "./symbol";

export type {
  Schematic,
  SchematicPin,
  BuiltinSymbolType,
  Constraints,
  ConstraintGroup,
  ConstraintMatch,
} from "./schematic";
export * as schematic from "./schematic";

export type { Layout } from "./layout";
export * as layout from "./layout";

export type {
  Circuit,
  CircuitInstance,
  CircuitConnection,
  CircuitInstanceParam,
  SpiceType,
  ExternalCircuit,
} from "./circuit";
export * as circuit from "./circuit";

export type {
  Sim,
  Analysis,
  AnalysisKind,
  DcAnalysis,
  AcAnalysis,
  TranAnalysis,
  NoiseAnalysis,
  SweepAnalysis,
  MonteAnalysis,
  Sweep,
  LogSweep,
  Control,
  ControlKind,
  Save,
  SaveMode,
  Param,
  SimOption,
  SimResult,
  AnalysisResult,
  OpResult,
  DcResult,
  AcResult,
  TranResult,
  NoiseResult,
  SweepResult,
  MonteResult,
  ComplexNum,
} from "./sim";
export * as sim from "./sim";

export type { Content, ContentKind } from "./content";
export * as content from "./content";

export * as validators from "./validators";

export * from "./pdk";
export * as pdk from "./pdk";

export * from "./pdk_utils";
export * as pdk_utils from "./pdk_utils";

// The generator <-> OA cell correspondence, read in both directions, plus the
// generated-ident codec both directions need. See `pdk_devices.ts` for why they
// live here rather than in the packages that use them.
export * from "./pdk_devices";
export * as pdk_devices from "./pdk_devices";

export * from "./metal";
export * as metal from "./metal";

export * from "./pins";
export * as pins from "./pins";

export * from "./parasitics";
export * as parasitics from "./parasitics";

export type { ProjectConfig } from "./config";
export * as config from "./config";

// Migration utilities
